import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Icon,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { format as formatFns } from "date-fns";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EditarContaAPagar from "layouts/payables/components/editData";
import EditarContaAReceber from "layouts/receivables/components/editData";
import { useEffect, useMemo, useState } from "react";
import { exportToExcel } from "react-json-to-excel";
import ContasService from "services/accounts";
import EmpresasService from "services/empresas";
import FinanceiroService from "services/financeiro";
import { ptBR_DataGrid } from "utils/datagrid";
import { dataCalendario } from "utils/dataCalendario";
import LancamentoDialog from "./lancamentoDialog";
import FechamentoPeriodo from "./fechamentoDialog";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

const ORIGEM = {
  lote: "Lote de baixa",
  conta: "Pagamento da conta",
  manual: "Lançamento manual",
  previsto: "Previsto",
};

const SITUACAO = {
  conciliado: { rotulo: "Conciliado", cor: "success" },
  pendente: { rotulo: "Pendente", cor: "warning" },
  futuro: { rotulo: "Futuro", cor: "default" },
};

function Resumo({ titulo, valor, detalhe }) {
  return (
    <Card sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography
        variant="h5"
        sx={{ color: Number(valor) < 0 ? "error.dark" : "text.primary" }}
      >
        {valor === null || valor === undefined ? "—" : brl(valor)}
      </Typography>
      {detalhe && (
        <Typography variant="caption" color="text.secondary">
          {detalhe}
        </Typography>
      )}
    </Card>
  );
}

/**
 * Movimentação bancária: entradas e saídas realizadas de uma conta bancária
 * (baixas de lote, contas pagas e lançamentos manuais) com o saldo após cada
 * uma, conciliação com o extrato e as contas futuras previstas na conta.
 */
export default function MovimentacaoBancaria() {
  const hoje = new Date();
  const [inicio, setInicio] = useState(
    formatFns(new Date(hoje.getFullYear(), hoje.getMonth(), 1), "yyyy-MM-dd")
  );
  const [fim, setFim] = useState(
    formatFns(
      new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0),
      "yyyy-MM-dd"
    )
  );
  const [empresa, setEmpresa] = useState("");
  const [account, setAccount] = useState("");
  const [situacao, setSituacao] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const [contas, setContas] = useState([]);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [selecionados, setSelecionados] = useState([]);
  const [dataConciliacao, setDataConciliacao] = useState(
    formatFns(hoje, "yyyy-MM-dd")
  );
  const [editarPagar, setEditarPagar] = useState(null);
  const [editarReceber, setEditarReceber] = useState(null);
  const [lancamento, setLancamento] = useState(null);

  const buscar = async () => {
    setLoading(true);
    setErro("");
    try {
      setDados(
        await FinanceiroService.movimentacao({
          dataInicio: inicio,
          dataFim: fim,
          empresa,
          account,
        })
      );
      setSelecionados([]);
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao carregar a movimentação.");
    }
    setLoading(false);
  };

  useEffect(() => {
    EmpresasService.list()
      .then(({ data }) => setEmpresas(data || []))
      .catch(() => {});
    ContasService.list()
      .then(({ data }) => setContas(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (inicio && fim) buscar();
  }, [inicio, fim, empresa, account]);

  const linhas = useMemo(() => {
    const todas = [...(dados?.linhas || []), ...(dados?.futuros || [])];
    return todas.filter(
      (l) =>
        !situacao ||
        l.situacao === situacao ||
        (l.tipo === "SALDO" && !situacao)
    );
  }, [dados, situacao]);

  const conciliar = async (conciliado) => {
    const movimentos = linhas
      .filter((l) => selecionados.includes(l.id))
      .map((l) => ({ id: l.id, account: l.account?._id }));
    if (!movimentos.length) return;
    try {
      await FinanceiroService.conciliar(
        movimentos,
        conciliado,
        dataConciliacao
      );
      buscar();
    } catch (e) {
      setErro(e?.error || "Erro ao conciliar.");
    }
  };

  const abrir = (row) => {
    if (row.contaTipo === "payables") setEditarPagar(row.contaId);
    else if (row.contaTipo === "receivables") setEditarReceber(row.contaId);
    else if (row.contaTipo === "manual" || row.contaTipo === "saldo")
      setLancamento({ _id: row.contaId });
  };

  // O diálogo de lançamento precisa do lançamento completo.
  useEffect(() => {
    if (!lancamento?._id || lancamento.tipo) return;
    FinanceiroService.lancamentos({}).then((lista) =>
      setLancamento(lista.find((l) => l._id === lancamento._id) || null)
    );
  }, [lancamento]);

  const exportar = () =>
    exportToExcel(
      [
        {
          sheetName: "Movimentação",
          details: linhas.map((l) => ({
            Data: dataCalendario(l.data),
            Conciliação: l.dataConciliacao
              ? dataCalendario(l.dataConciliacao)
              : "",
            Histórico: l.historico,
            "Cliente/Fornecedor": l.pessoa || "",
            Natureza: l.natureza || "",
            Origem:
              l.tipo === "SALDO" ? "Saldo informado" : ORIGEM[l.origem] || "",
            Entrada: l.tipo === "E" ? l.valor : "",
            Saída: l.tipo === "S" ? l.valor : "",
            "Saldo após": l.saldoApos ?? "",
            Situação: SITUACAO[l.situacao]?.rotulo || "",
          })),
        },
      ],
      `movimentacao_${inicio}_${fim}`,
      true
    );

  const filtro = { size: "small", InputLabelProps: { shrink: true } };
  const t = dados?.realizado;

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        {editarPagar && (
          <EditarContaAPagar
            setShowEditState={() => setEditarPagar(null)}
            currentIdAction={editarPagar}
            fetchData={buscar}
          />
        )}
        {editarReceber && (
          <EditarContaAReceber
            setShowEditState={() => setEditarReceber(null)}
            currentIdAction={editarReceber}
            fetchData={buscar}
          />
        )}
        <LancamentoDialog
          open={Boolean(lancamento?.tipo || lancamento?.novo)}
          lancamento={lancamento?.novo ? null : lancamento}
          padrao={{ account, tipo: lancamento?.tipoNovo || "SALDO" }}
          onClose={() => setLancamento(null)}
          onSalvo={() => {
            setLancamento(null);
            buscar();
          }}
        />

        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <MDTypography variant="h6" color="white" mt={1}>
              Movimentação bancária
            </MDTypography>
            <Box display="flex" gap={1} alignItems="center">
              <FechamentoPeriodo onAlterado={buscar} />
              <MDButton
                variant="contained"
                onClick={() => setLancamento({ novo: true, tipoNovo: "SALDO" })}
              >
                Informar saldo
              </MDButton>
              <MDButton
                variant="contained"
                onClick={() => setLancamento({ novo: true, tipoNovo: "S" })}
              >
                Lançamento manual
              </MDButton>
            </Box>
          </MDBox>

          <MDBox p={3}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                label="De"
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                {...filtro}
              />
              <TextField
                label="Até"
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                {...filtro}
              />
              <TextField
                label="Empresa"
                select
                SelectProps={{ native: true }}
                value={empresa}
                onChange={(e) => {
                  setEmpresa(e.target.value);
                  setAccount("");
                }}
                sx={{ minWidth: 170 }}
                {...filtro}
              >
                <option value="">Todas</option>
                {empresas.map((e) => (
                  <option key={e._id} value={e._id}>
                    {e.nomeFantasia || e.razaoSocial}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Conta bancária"
                select
                SelectProps={{ native: true }}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                sx={{ minWidth: 190 }}
                {...filtro}
              >
                <option value="">Todas</option>
                {contas
                  .filter(
                    (c) => !empresa || (c.empresa?._id || c.empresa) === empresa
                  )
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nome}
                    </option>
                  ))}
              </TextField>
              <TextField
                label="Situação"
                select
                SelectProps={{ native: true }}
                value={situacao}
                onChange={(e) => setSituacao(e.target.value)}
                sx={{ minWidth: 150 }}
                {...filtro}
              >
                <option value="">Todas</option>
                <option value="conciliado">Conciliados</option>
                <option value="pendente">Pendentes</option>
                <option value="futuro">Futuros</option>
              </TextField>
              <Box flexGrow={1} />
              <Button
                variant="contained"
                sx={{ color: "white !important" }}
                onClick={exportar}
                disabled={!dados}
              >
                Exportar para Excel
              </Button>
            </Box>

            {!account && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                Escolha uma conta bancária para ver o saldo após cada movimento.
              </Typography>
            )}
            {erro && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {erro}
              </Alert>
            )}

            {dados && (
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6} md={3}>
                  <Resumo titulo="Saldo inicial" valor={dados.saldoInicial} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Resumo titulo="Entradas" valor={t?.entradas} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Resumo titulo="Saídas" valor={t ? -t.saidas : null} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Resumo titulo="Saldo final" valor={dados.saldoFinal} />
                </Grid>
              </Grid>
            )}

            <Box display="flex" gap={2} alignItems="center" mt={3} mb={1}>
              <Typography variant="body2" color="text.secondary">
                {selecionados.length} selecionado(s)
              </Typography>
              <TextField
                label="Data da conciliação"
                type="date"
                value={dataConciliacao}
                onChange={(e) => setDataConciliacao(e.target.value)}
                {...filtro}
              />
              <Button
                variant="contained"
                sx={{ color: "white !important" }}
                disabled={!selecionados.length}
                onClick={() => conciliar(true)}
              >
                Conciliar
              </Button>
              <Button
                disabled={!selecionados.length}
                onClick={() => conciliar(false)}
              >
                Desfazer conciliação
              </Button>
            </Box>

            <DataGrid
              rows={linhas}
              autoHeight
              density="compact"
              loading={loading}
              localeText={ptBR_DataGrid}
              checkboxSelection
              disableRowSelectionOnClick
              isRowSelectable={({ row }) =>
                row.situacao !== "futuro" && row.tipo !== "SALDO"
              }
              rowSelectionModel={selecionados}
              onRowSelectionModelChange={setSelecionados}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[50, 100]}
              getRowClassName={({ row }) =>
                row.tipo === "SALDO"
                  ? "linha-saldo"
                  : row.situacao === "futuro"
                  ? "linha-futuro"
                  : ""
              }
              sx={{
                ".linha-saldo": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  fontWeight: 600,
                },
                ".linha-futuro": {
                  color: "text.secondary",
                  fontStyle: "italic",
                },
              }}
              columns={[
                {
                  field: "data",
                  headerName: "Data",
                  minWidth: 100,
                  valueGetter: ({ row }) => dataCalendario(row.data),
                },
                {
                  field: "dataConciliacao",
                  headerName: "Conciliação",
                  minWidth: 100,
                  valueGetter: ({ row }) =>
                    row.dataConciliacao
                      ? dataCalendario(row.dataConciliacao)
                      : "",
                },
                {
                  field: "historico",
                  headerName: "Histórico",
                  flex: 1.5,
                  valueGetter: ({ row }) =>
                    row.tipo === "SALDO"
                      ? `${row.historico}${
                          row.diferenca
                            ? ` (diferença ${brl(row.diferenca)})`
                            : ""
                        }`
                      : [row.historico, row.pessoa].filter(Boolean).join(" · "),
                },
                { field: "natureza", headerName: "Natureza", flex: 1 },
                {
                  field: "origem",
                  headerName: "Origem",
                  minWidth: 130,
                  valueGetter: ({ row }) =>
                    row.tipo === "SALDO"
                      ? "Saldo informado"
                      : ORIGEM[row.origem] || "",
                },
                {
                  field: "entrada",
                  headerName: "Entrada",
                  minWidth: 110,
                  align: "right",
                  headerAlign: "right",
                  valueGetter: ({ row }) =>
                    row.tipo === "E" ? brl(row.valor) : "",
                },
                {
                  field: "saida",
                  headerName: "Saída",
                  minWidth: 110,
                  align: "right",
                  headerAlign: "right",
                  valueGetter: ({ row }) =>
                    row.tipo === "S" ? brl(row.valor) : "",
                },
                {
                  field: "saldoApos",
                  headerName: "Saldo após",
                  minWidth: 120,
                  align: "right",
                  headerAlign: "right",
                  valueGetter: ({ row }) =>
                    row.saldoApos === undefined || row.saldoApos === null
                      ? ""
                      : brl(row.saldoApos),
                },
                {
                  field: "situacao",
                  headerName: "Situação",
                  minWidth: 110,
                  renderCell: ({ row }) =>
                    row.tipo === "SALDO" ? null : (
                      <Chip
                        size="small"
                        label={SITUACAO[row.situacao]?.rotulo}
                        color={SITUACAO[row.situacao]?.cor}
                        variant="outlined"
                      />
                    ),
                },
                {
                  field: "acoes",
                  headerName: "",
                  sortable: false,
                  minWidth: 60,
                  renderCell: ({ row }) =>
                    row.contaId ? (
                      <MDButton
                        variant="gradient"
                        color="info"
                        iconOnly
                        onClick={() => abrir(row)}
                      >
                        <Icon fontSize="small">edit</Icon>
                      </MDButton>
                    ) : null,
                },
              ]}
            />
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
